<template>
  <section
    :style="{ backgroundColor: getPageBackgroundColor }"
    :class="{ 'vx-custom-background': hasCustomBackgroundColor }"
  >
    <section
      v-for="(slice, index) in parsedSlices"
      :key="'slice-' + index"
      :class="[
        sliceOverflowAuto(slice.slice_type, index, parsedSlices.length) ? 'overflow-auto' : '',
        sliceClassName(slice.slice_type)
      ]"
    >
      <SlicesSeparator
        :margin-top="marginTop(index, slice.primary.margin_top, slice.slice_type)"
        :margin-bottom="marginBottom(slice.primary.margin_bottom, slice.slice_type)"
      >
        <!-- Anchor Id template -->
        <template v-if="slice.slice_type === 'anchor_id'">
          <AnchorId :slice="slice" />
        </template>

        <!-- Collections Carousel template -->
        <template v-if="slice.slice_type === 'collections_carousel'">
          <CollectionsCarousel :slice="slice"></CollectionsCarousel>
        </template>

        <!-- FAQ template -->
        <template v-else-if="slice.slice_type === 'faq'">
          <FAQ :slice="slice"></FAQ>
        </template>

        <!-- Header template -->
        <template v-else-if="slice.slice_type === 'header'">
          <Header :slice="slice"></Header>
        </template>

        <!-- Header custom template -->
        <template v-else-if="slice.slice_type === 'header_custom'">
          <HeaderCustom :slice="slice" />
        </template>

        <!-- New Header template -->
        <template v-else-if="slice.slice_type === 'header_1'">
          <ExtendedHeader :slice="slice" />
        </template>

        <!-- Hero Header template -->
        <template v-else-if="slice.slice_type === 'hero_header'">
          <HeroHeader :slice="slice"></HeroHeader>
        </template>

        <!-- Hero Plans template -->
        <template v-else-if="slice.slice_type === 'hero_plans'">
          <HeroPlans :slice="slice"></HeroPlans>
        </template>

        <!-- Slider displaying two versions of the same image template -->
        <template v-else-if="slice.slice_type === 'mockup_slider'">
          <MockupSection :slice="slice"></MockupSection>
        </template>

        <!-- Niches & Topics Carousel template -->
        <template v-else-if="slice.slice_type === 'niches_carousel'">
          <NichesCarousel :slice="slice"></NichesCarousel>
        </template>

        <!-- Items featuring an image and some text as well as a cta in a grid template -->
        <template v-else-if="slice.slice_type === 'info_grid'">
          <InfoGrid :slice="slice"></InfoGrid>
        </template>

        <!-- Info tabs -->
        <template v-else-if="slice.slice_type === 'info_tabs'">
          <InfoTabs :slice="slice"></InfoTabs>
        </template>

        <!-- Find Best Designs Block Template -->
        <template v-else-if="slice.slice_type === 'find_best_designs_blocks'">
          <FindBestDesignsBlocks :slice="slice" />
        </template>

        <!-- Fixed Plans template -->
        <template v-else-if="slice.slice_type === 'fixed_plans'">
          <FixedPlans :slice="slice"></FixedPlans>
        </template>

        <!-- PODs template -->
        <template v-else-if="slice.slice_type === 'pod'">
          <PODs :slice="slice"></PODs>
        </template>

        <!-- Purchase Plan template -->
        <template v-else-if="slice.slice_type === 'purchase_plan'">
          <PurchasePlan :slice="slice"></PurchasePlan>
        </template>

        <!-- Left Right Image template -->
        <template
          v-else-if="slice.slice_type === 'right_image' || slice.slice_type === 'left_image'"
        >
          <LeftRightImage
            :is-right="slice.slice_type === 'right_image'"
            :slice="slice"
          ></LeftRightImage>
        </template>

        <!-- Search Grid Or Carousel template -->
        <template v-else-if="slice.slice_type === 'search_grid'">
          <SearchGridCarousel :slice="slice"></SearchGridCarousel>
        </template>

        <!-- Styles Carousel template -->
        <template v-else-if="slice.slice_type === 'styles_carousel'">
          <StylesCarousel :slice="slice"></StylesCarousel>
        </template>

        <!-- Testimonials template -->
        <template v-else-if="slice.slice_type === 'testimonials'">
          <Testimonials :slice="slice"></Testimonials>
        </template>

        <!-- Text template -->
        <template v-else-if="slice.slice_type === 'text'">
          <TextColumns :slice="slice"></TextColumns>
        </template>

        <!-- Trusted by template -->
        <template v-else-if="slice.slice_type === 'trusted_by'">
          <TrustedBy></TrustedBy>
        </template>

        <!-- Vectors Grid template -->
        <template v-else-if="slice.slice_type === 'vectors_grid'">
          <VectorsGrid :slice="slice"></VectorsGrid>
        </template>

        <!-- Video template -->
        <template v-else-if="slice.slice_type === 'video'">
          <Video :slice="slice"></Video>
        </template>

        <!-- Hero with Tabs template -->
        <template v-else-if="slice.slice_type === 'hero_tabs'">
          <HeroTabs :slice="slice"></HeroTabs>
        </template>

        <!-- Plans compare table template -->
        <template v-else-if="slice.slice_type === 'compare_plans_table'">
          <PlansComparisonTable :slice="slice"></PlansComparisonTable>
        </template>

        <!-- Plans compareV2 table template -->
        <template v-else-if="slice.slice_type === 'compare_plans_table_v2'">
          <PlansComparisonTableV2 :slice="slice"></PlansComparisonTableV2>
        </template>

        <!-- Plans Get Graphics template -->
        <template v-else-if="slice.slice_type === 'plans_get_graphics'">
          <PlansGetGraphics :slice="slice"></PlansGetGraphics>
        </template>

        <!-- Plans Icon Block template -->
        <template v-else-if="slice.slice_type === 'plans_icon_block'">
          <PlansIconBlock :slice="slice"></PlansIconBlock>
        </template>

        <!-- Carousel Header template -->
        <template v-else-if="slice.slice_type === 'carousel_header'">
          <CarouselHeader :slice="slice"></CarouselHeader>
        </template>

        <!-- Carousel template -->
        <template v-else-if="slice.slice_type === 'carousel'">
          <Carousel :slice="slice"></Carousel>
        </template>

        <!-- Button Block template -->
        <template v-else-if="slice.slice_type === 'button'">
          <Button :slice="slice" />
        </template>

        <!-- How To Block template -->
        <template v-else-if="slice.slice_type === 'how_to'">
          <HowTo :slice="slice" />
        </template>

        <!-- Tabs Grid Experience template -->
        <template v-else-if="slice.slice_type === 'tabs_grid_experience'">
          <GridTabsExperience :slice="slice" />
        </template>

        <!-- Makers Block template -->
        <template v-else-if="slice.slice_type === 'makers'">
          <Makers :slice="slice" />
        </template>

        <!-- You Can Find Block template -->
        <template v-else-if="slice.slice_type === 'you_can_find'">
          <YouCanFind :slice="slice" />
        </template>

        <!-- Title Block template -->
        <template v-else-if="slice.slice_type === 'title'">
          <Title :slice="slice" />
        </template>

        <!-- Columns Block template -->
        <template v-else-if="slice.slice_type === 'columns'">
          <Columns :slice="slice" />
        </template>

        <!-- Space Block template -->
        <template v-else-if="slice.slice_type === 'space'">
          <Space :slice="slice" />
        </template>

        <!-- Slice Steps Block template -->
        <template v-else-if="slice.slice_type === 'steps'">
          <Steps :slice="slice" />
        </template>

        <!-- Features Block template -->
        <template v-else-if="slice.slice_type === 'features'">
          <Features :slice="slice" />
        </template>

        <!-- Comparison Table template -->
        <template v-else-if="slice.slice_type === 'comparison_table'">
          <ComparisonTable :slice="slice" />
        </template>

        <!-- Carousel Custom -->
        <template v-else-if="slice.slice_type === 'carousel_custom'">
          <CarouselCustom :slice="slice" />
        </template>

        <!-- Home Header Default -->
        <template v-else-if="slice.slice_type === 'home_header_default'">
          <ChooseExperience :slice="slice" />
        </template>

        <!-- Home Header Merch Design -->
        <template v-else-if="slice.slice_type === 'home_header_merch_design'">
          <BannerContent :slice="slice" />
        </template>

        <!-- Coupon Code -->
        <template v-else-if="slice.slice_type === 'coupon_code'">
          <CouponCode :slice="slice" />
        </template>

        <!-- Home Header V2 -->
        <template v-else-if="slice.slice_type === 'home_header_v2'">
          <BannerContentV2 :slice="slice" />
        </template>

        <!-- Home Header V3 -->
        <template v-else-if="slice.slice_type === 'home_header_v3'">
          <HomeHeaderV3 :slice="slice" />
        </template>

        <!-- Banner -->
        <template v-else-if="slice.slice_type === 'banner'">
          <Banner :slice="slice" />
        </template>

        <!-- Purchase Banner -->
        <template v-else-if="slice.slice_type === 'purchase_banner'">
          <PurchaseBanner :slice="slice" />
        </template>

        <!-- Plans Header -->
        <template v-else-if="slice.slice_type === 'plans_header'">
          <PlansHeader :slice="slice" />
        </template>

        <!-- Search Bar -->
        <template v-else-if="slice.slice_type === 'search_bar'">
          <SearchBar :slice="slice" />
        </template>

        <!-- Tools Contents -->
        <template v-else-if="slice.slice_type === 'tools_contents'">
          <ToolsContents :slice="slice" />
        </template>

        <!-- Tools Contents -->
        <template v-else-if="slice.slice_type === 'typeform'">
          <Typeform :slice="slice" />
        </template>

        <!-- Image Slice -->
        <template v-else-if="slice.slice_type === 'image'">
          <ImageSlice :slice="slice" />
        </template>

        <!-- Price Area -->
        <template v-else-if="slice.slice_type === 'price_area'">
          <PriceArea :slice="slice" />
        </template>

        <!-- Simulate Earnings -->
        <template v-else-if="slice.slice_type === 'simulate_earnings'">
          <SimulateEarnings :slice="slice" />
        </template>

        <!-- Price Area Group -->
        <template v-else-if="slice.slice_type === 'price_area_group'">
          <PriceAreaGroup :slice="slice" />
        </template>
      </SlicesSeparator>
    </section>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';

// Imports for all slices
import SlicesSeparator from '@/components/Prismic/Landings/SlicesSeparator';
const AnchorId = () => import('@/components/Prismic/Landings/Slices/AnchorId.vue');
const Button = () => import('@/components/Prismic/Landings/Slices/Button.vue');
const Carousel = () => import('@/components/Prismic/Landings/Slices/Carousel.vue');
const CarouselCustom = () =>
  import('@/components/Prismic/Landings/Slices/CarouselCustom/index.vue');
const CarouselHeader = () => import('@/components/Prismic/Landings/Slices/CarouselHeader.vue');
const ChooseExperience = () => import('@/components/Prismic/Landings/Slices/ChooseExperience.vue');
const CollectionsCarousel = () =>
  import('@/components/Prismic/Landings/Slices/CollectionsCarousel.vue');
const Columns = () => import('@/components/Prismic/Landings/Slices/Columns.vue');
const ComparisonTable = () => import('@/components/Prismic/Landings/Slices/ComparisonTable.vue');
const CouponCode = () => import('@/components/Prismic/Landings/Slices/CouponCode.vue');
const ExtendedHeader = () => import('@/components/Prismic/Landings/Slices/ExtendedHeader.vue');
const FAQ = () => import('@/components/Prismic/Landings/Slices/FAQ.vue');
const FixedPlans = () => import('@/components/Prismic/Landings/Slices/FixedPlans.vue');
const Features = () => import('@/components/Prismic/Landings/Slices/Features.vue');
const FindBestDesignsBlocks = () =>
  import('@/components/Prismic/Landings/Slices/FindBestDesignsBlocks.vue');
const GridTabsExperience = () => import('@/components/Prismic/Landings/Slices/GridTabsExperience');
const Header = () => import('@/components/Prismic/Landings/Slices/Header.vue');
const HeaderCustom = () => import('@/components/Prismic/Landings/Slices/HeaderCustom.vue');
const HeroHeader = () => import('@/components/Prismic/Landings/Slices/HeroHeader.vue');
const HeroPlans = () => import('@/components/Prismic/Landings/Slices/HeroPlans.vue');
const HeroTabs = () => import('@/components/Prismic/Landings/Slices/HeroTabs.vue');
const HowTo = () => import('@/components/Prismic/Landings/Slices/HowTo.vue');
const HomeHeaderV3 = () => import('@/components/Prismic/Landings/Slices/HomeHeaderV3.vue');
const InfoGrid = () => import('@/components/Prismic/Landings/Slices/InfoGrid.vue');
const InfoTabs = () => import('@/components/Prismic/Landings/Slices/InfoTabs');
const ImageSlice = () => import('@/components/Prismic/CommonSlices/Image.vue');
const LeftRightImage = () => import('@/components/Prismic/Landings/Slices/LeftRightImage.vue');
const Makers = () => import('@/components/Prismic/Landings/Slices/Makers.vue');
const MockupSection = () => import('@/components/Prismic/Landings/Slices/MockupSection.vue');
const NichesCarousel = () => import('@/components/Prismic/Landings/Slices/NichesCarousel.vue');
const PlansComparisonTable = () =>
  import('@/components/Prismic/Landings/Slices/PlansComparisonTable');
const PlansComparisonTableV2 = () =>
  import('@/components/Prismic/Landings/Slices/PlansComparisonTableV2/index.vue');
const PlansGetGraphics = () => import('@/components/Prismic/Landings/Slices/PlansGetGraphics.vue');
const PlansIconBlock = () => import('@/components/Prismic/Landings/Slices/PlansIconBlock.vue');
const PlansHeader = () => import('@/components/Prismic/Landings/Slices/PlansHeader.vue');
const PODs = () => import('@/components/Prismic/Landings/Slices/PODs.vue');
const PurchasePlan = () => import('@/components/Prismic/Landings/Slices/PurchasePlan.vue');
const PurchaseBanner = () => import('@/components/Prismic/Landings/Slices/PurchaseBanner.vue');
const SearchGridCarousel = () =>
  import('@/components/Prismic/Landings/Slices/SearchGridCarousel.vue');
const SearchBar = () => import('@/components/Prismic/Landings/Slices/SearchBar.vue');
const SimulateEarnings = () => import('@/components/Prismic/Landings/Slices/SimulateEarnings.vue');
const Space = () => import('@/components/Prismic/Landings/Slices/Space.vue');
const StylesCarousel = () => import('@/components/Prismic/Landings/Slices/StylesCarousel.vue');
const Steps = () => import('@/components/Prismic/Landings/Slices/Steps/index.vue');
const Testimonials = () => import('@/components/Prismic/Landings/Slices/Testimonials.vue');
const TextColumns = () => import('@/components/Prismic/Landings/Slices/TextColumns.vue');
const Title = () => import('@/components/Prismic/Landings/Slices/Title.vue');
const TrustedBy = () => import('@/components/Prismic/Landings/Slices/TrustedBy.vue');
const VectorsGrid = () => import('@/components/Prismic/Landings/Slices/VectorsGrid.vue');
const Video = () => import('@/components/Prismic/Landings/Slices/Video.vue');
const YouCanFind = () => import('@/components/Prismic/Landings/Slices/YouCanFind.vue');
const BannerContent = () => import('@/components/Prismic/Landings/Slices/BannerContent.vue');
const BannerContentV2 = () => import('@/components/Prismic/Landings/Slices/BannerContentV2.vue');
const Banner = () => import('@/components/Prismic/Landings/Slices/Banner.vue');
const ToolsContents = () => import('@/components/Prismic/Landings/Slices/ToolsContents/index.vue');
const Typeform = () => import('@/components/Prismic/Landings/Slices/Typeform.vue');
const PriceArea = () => import('@/components/Prismic/Landings/Slices/PriceArea.vue');
const PriceAreaGroup = () => import('@/components/Prismic/Landings/Slices/PriceAreaGroup.vue');

export default {
  name: 'SlicesBlock',
  components: {
    AnchorId,
    Banner,
    Button,
    Carousel,
    CarouselCustom,
    CarouselHeader,
    ChooseExperience,
    CollectionsCarousel,
    Columns,
    ComparisonTable,
    CouponCode,
    ExtendedHeader,
    FAQ,
    FixedPlans,
    Features,
    FindBestDesignsBlocks,
    GridTabsExperience,
    Header,
    HeaderCustom,
    HeroHeader,
    HeroPlans,
    HeroTabs,
    HowTo,
    HomeHeaderV3,
    InfoGrid,
    InfoTabs,
    ImageSlice,
    LeftRightImage,
    Makers,
    MockupSection,
    NichesCarousel,
    PlansComparisonTable,
    PlansComparisonTableV2,
    PlansGetGraphics,
    PlansIconBlock,
    PlansHeader,
    PODs,
    PriceArea,
    PriceAreaGroup,
    PurchasePlan,
    PurchaseBanner,
    SearchGridCarousel,
    SearchBar,
    SlicesSeparator,
    SimulateEarnings,
    Space,
    StylesCarousel,
    Steps,
    Testimonials,
    TextColumns,
    Title,
    TrustedBy,
    VectorsGrid,
    Video,
    YouCanFind,
    BannerContent,
    BannerContentV2,
    ToolsContents,
    Typeform
  },
  props: {
    slices: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      defaultMargin: 50,
      noMarginSlices:
        // eslint-disable-next-line max-len
        /anchor_id|carousel|space|title|tabs_grid_experience|home_header_default|home_header_v2|coupon_code/
    };
  },
  async fetch() {
    const pageConfiguration = this.slices.find(
      (slice) => slice.slice_type === 'page_configuration'
    );
    if (pageConfiguration) {
      await this.$store.dispatch(
        'config/SET_PRISMIC_PAGE_CONFIGURATION',
        pageConfiguration.primary
      );
    }
  },
  computed: {
    ...mapGetters('config', ['getPageBackgroundColor', 'hasCustomBackgroundColor']),
    parsedSlices() {
      return this.slices.filter((slice) => slice.slice_type !== 'page_configuration');
    }
  },
  beforeDestroy() {
    this.$store.dispatch('config/SET_PRISMIC_PAGE_CONFIGURATION', {});
  },

  methods: {
    prevSlideIsCarouselHeader(sliceType, index) {
      let prevSlideIsCarouselHeader = false;
      if (sliceType === 'search_grid' && index > 0) {
        const previousSlide = this.parsedSlices[index - 1];
        const prevSliceType = previousSlide.slice_type;
        prevSlideIsCarouselHeader = prevSliceType == 'carousel_header';
      }
      return prevSlideIsCarouselHeader;
    },
    margin(margin) {
      return margin || margin == 0 ? margin : this.defaultMargin;
    },
    marginTop(index, margin, sliceType) {
      const prevIsCarouselHeader = this.prevSlideIsCarouselHeader(sliceType, index);
      return index != 0 && !this.noMarginSlices.test(sliceType) && !prevIsCarouselHeader
        ? this.margin(margin)
        : 0;
    },
    marginBottom(margin, sliceType) {
      return !/carousel_header/.test(sliceType) && !this.noMarginSlices.test(sliceType)
        ? this.margin(margin)
        : 0;
    },
    sliceOverflowAuto(sliceType, index, slicesLength) {
      return index === slicesLength - 1 && sliceType !== 'search_bar';
    },
    sliceClassName(sliceType) {
      return `slice-${sliceType.split('_').join('-')}`;
    }
  }
};
</script>
<style lang="scss" scoped>
.slice-anchor-id:first-child + [class*='slice-'] .slices-separator {
  margin-top: 0;
}
</style>
